/* eslint class-methods-use-this: 0 */
import { cloneDeep, values } from 'lodash';
import Api from 'api/v2/Api';
import CollectionApi from 'api/v2/CollectionApi';
import Constants from 'client/util/Constants';
import axios from 'axios';

const {
  linux,
  filterNames,
  macOSX,
  windows,
} = Constants;

/**
 * The BoundItemsApi queries all bound items so that sorting and searching is
 * available when showing bound items in an ItemTable. Unfortunately the server
 * side endpoints that return bound items cannot be used to search and sort
 * them. As a result, the client side code uses the BoundItemsApi to build the
 * dataset of bound items, in memory, so that searching and sorting are
 * available. If the number of bound items exceeds 1000 then the ItemTable will
 * disable the show bound items checkbox, preventing the admin user from
 * viewing only bound items.
 * @todo A downside of this approach is that all bound models are loaded into
 * memory on the client side. On mobile devices this will potentially be a
 * large impact on performance. We *WILL* need to review this functionality
 * when the admin console is supported on mobile devices.
 */
export default class BoundItemsApi extends CollectionApi {
  /**
   * Override method from base class to pass dummy parameters to its
   * constructor. This class is a singleton instance used by the
   * CollectionModule and its baseUrl isn't known until it is used and its
   * default parameters aren't used. The maximum fetch limit is set to 1000.
   */
  constructor() {
    super('/base/url/is/not/available/yet', {}, 1000);
  }

  /**
   * Initalize the super class and then get the initial set of bound items from
   * the specified URL. The bound items will be decorated on the server side
   * and then returned to the client. The bound items will serve as the in
   * memory dataset that will be manipulated in fetch() calls.
   */
  initialize(baseUrl, defaultParams) {
    this.baseUrl = baseUrl;

    // Save the specified default parameters so they can be reused later in
    // fetch() when applying the search and sort to the set of bound items.
    this.defaultParams = cloneDeep(defaultParams);

    return new Promise((resolve, reject) => {
      const mesg = 'Failed to initialize bound items!';

      super.initialize()
        .then(() => {
          const params = {
            details: 'v1',
            skip: 0,
            limit: this.maxFetchLimit,
          };

          if (!this.isDetailsParamSupported(baseUrl)) {
            delete params.details;
          }

          // There is no need to paginate if there are more than 1000 bound
          // items because the show bound items feature will be disabled in
          // this case.

          axios
            .get(`${this.baseUrl}?${this.param(params)}`)
            .then((resp) => {
              if (resp && typeof Array.isArray(resp.data)) {
                resolve({
                  results: resp.data,
                  totalCount: resp.data.length,
                });
              } else {
                Api.handleError(reject, mesg);
              }
            })
            .catch((error) => {
              Api.handleError(reject, mesg, error);
            });
        })
        .catch((error) => {
          Api.handleError(reject, mesg, error);
        });
    });
  }

  /**
   * @todo What other filters do we need to support currently? Should we throw an error when the
   * caller users an unsupported filter?
   */
  applyFilters(items, filters) {
    if (filters && filters.length) {
      const appliedFilters = filters.filter(filter => (filter.value !== ''));

      if (!appliedFilters.length) {
        return items;
      }

      let filteredItems = items;

      appliedFilters.forEach((filter) => {
        let filterFunction;
        const filterTerms = filter.name.split('.');
        if (filter.name === filterNames.os && filter.value === linux) {
          filterFunction = item => (item[filter.name] !== macOSX && item[filter.name] !== windows);
        } else if (filter.operator === '$regex') {
          // Convert a string like '/Windows/i' to the regexp /Windows/i
          // SEE: https://stackoverflow.com/questions/874709/converting-user-input-string-to-regular-expression#874742
          const [, pattern, flags] = String(filter.value).match(new RegExp('^/(.*?)/([gimy]*)$'));
          const regexp = new RegExp(pattern, flags);
          filterFunction = item => Boolean(String(item[filter.name]).match(regexp));
        } else if (filter.operator === '$ne' || filter.operator === 'ne') {
          filterFunction = item => (item[filter.name] !== filter.value);
        } else if (filter.operator === 'in' && filter.name === filterNames.osMetaFamily) {
          const firstLevel = filterTerms[0];
          const secondLevel = 'osMetaFamily';
          filterFunction = item => (
            filter.value.includes(item[firstLevel][secondLevel].toUpperCase())
          );
        } else if (filterTerms.length === 2) {
          const firstLevel = filterTerms[0];
          const secondLevel = filterTerms[1];
          filterFunction = item => (item[firstLevel][secondLevel] === filter.value);
        } else {
          filterFunction = item => (item[filter.name] === filter.value);
        }

        filteredItems = filteredItems.filter(filterFunction);
      });

      return filteredItems;
    }

    return items;
  }

  /**
   * Filter items by performing a simple case insensitive check to see if the
   * specified search term is available in any of the search fields of each
   * item.
   */
  applySearch(items, searchTerm) {
    if (!searchTerm) {
      return items;
    }

    let searchFields;

    if (this.defaultParams.search) {
      searchFields = this.defaultParams.search.fields;
    } else if (this.defaultParams.filter) {
      searchFields = this.defaultParams.filter
        .filter(f => f.operator === 'search')
        .map(f => f.name);
    }

    const regexp = new RegExp(String(searchTerm), 'i');

    const filteredItems = items.filter((item) => {
      let fieldMatched = false;

      searchFields.forEach((field) => {
        if (String(item[field]).match(regexp)) {
          fieldMatched = true;
        }
      });

      return fieldMatched;
    });

    return filteredItems;
  }

  applySort(items, sort) {
    if (!sort) {
      return items;
    }

    const sortOrder = sort.match(/^-/) ? 'desc' : 'asc';
    const sortField = sort.replace(/^-/, '');

    items.sort((a, b) => {
      const aValue = String(a[sortField] || '').toLowerCase();
      const bValue = String(b[sortField] || '').toLowerCase();
      let retValue = 0;

      if (sortOrder === 'asc') {
        if (aValue < bValue) {
          retValue = -1;
        } else if (aValue > bValue) {
          retValue = 1;
        }
      } else if (sortOrder === 'desc') {
        if (aValue < bValue) {
          retValue = 1;
        } else if (aValue > bValue) {
          retValue = -1;
        }
      }

      return retValue;
    });

    return items;
  }

  /**
   * Override method from base class because it isn't necessary to make a GET
   * request to the bound items endpoint to determine if any of the specified
   * IDs are bound when displaying the bound items.
   */
  checkForBoundItems() {
    return new Promise((resolve) => {
      resolve([]);
    });
  }

  fetchAll(searchTerm, boundItems) {
    return new Promise((resolve) => {
      this
        .fetch(0, Object.keys(boundItems).length, '', searchTerm, boundItems)
        .then((items) => {
          resolve(items.results);
        });
    });
  }

  /**
   * Perform a fetch() request by applying the search and sort to the set of bound
   * items and returning the requested subset of the filtered and sorted data
   * set.
   */
  fetch(skip, limit, sort, searchTerm, boundItems, filters) {
    return new Promise((resolve) => {
      let items = values(boundItems);

      items = this.applyFilters(items, filters);
      items = this.applySearch(items, searchTerm);
      items = this.applySort(items, sort);

      const totalCount = items.length;
      items = items.slice(skip, skip + limit);

      resolve({
        results: items,
        totalCount,
      });
    });
  }

  /**
   * Some URLs do not support the details=v1 query string parameter. Inform the
   * caller of those cases here.
   * @todo Add cases for other bound items URLs where the details=v1 query
   * string parameter isn't supported.
   * @see https://github.com/TheJumpCloud/SI/blob/master/routes/webui/api/v2/graph.js#L599
   */
  isDetailsParamSupported(baseUrl) {
    const unsupportedEndpoints = [
      'applications',
      'directories',
      'radiusservers',
    ];
    return unsupportedEndpoints.every(endpoint => !baseUrl.match(new RegExp(`/${endpoint}$`)));
  }
}
